import React, { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { useAuth } from "@/context/AuthContext";
import InviteUserDialog from "@/components/InviteUserDialog";
import EditUserDialog from "@/components/admin/EditUserDialog";
import ActiveInvitesPanel from "@/components/admin/ActiveInvitesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    apiListUsers,
    apiListProjects,
    apiListSkills,
    apiResendInvite,
    apiRevokeInvite,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import {
    UserPlus,
    Search,
    RefreshCw,
    Copy,
    XCircle,
    Pencil,
    Lock,
} from "lucide-react";

export default function AdminUsers() {
    const { user: actor } = useAuth();
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [u, p, s] = await Promise.all([
                apiListUsers(),
                apiListProjects(),
                apiListSkills(),
            ]);
            setUsers(u);
            setProjects(p);
            setSkills(s);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter(
            (u) =>
                u.full_name?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q) ||
                u.role?.toLowerCase().includes(q),
        );
    }, [users, query]);

    async function resend(u) {
        try {
            const res = await apiResendInvite(u.user_id);
            const link = `${window.location.origin}/accept-invite/${res.invite_token}`;
            await navigator.clipboard.writeText(link).catch(() => {});
            toast.success("New invite link copied to clipboard");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function revoke(u) {
        if (!window.confirm(`Revoke invite for ${u.email}?`)) return;
        try {
            await apiRevokeInvite(u.user_id);
            toast.success("Invite revoked");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <Layout active="users">
            <PageHeader
                title="Users"
                subtitle="Manage members, invites, roles, skills, and project access."
                actions={
                    <>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ntt-muted" />
                            <Input
                                data-testid="users-search"
                                placeholder="Search name, email, role"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="h-9 pl-8 w-64"
                            />
                        </div>
                        <Button
                            onClick={() => setInviteOpen(true)}
                            data-testid="open-invite-dialog-btn"
                            className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            <UserPlus className="h-4 w-4 mr-1.5" />
                            Invite user
                        </Button>
                    </>
                }
            />

            <ActiveInvitesPanel users={users} onChanged={load} />

            <div
                className="bg-white border border-ntt-border rounded-xl overflow-hidden"
                data-testid="users-table"
            >
                <div className="grid grid-cols-[minmax(200px,2fr)_minmax(160px,1.4fr)_120px_100px_120px_160px] gap-4 px-5 py-3 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                    <div>Name</div>
                    <div>Email</div>
                    <div>Role</div>
                    <div>Active</div>
                    <div>Invite</div>
                    <div className="text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-ntt-muted">
                        Loading users…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-sm text-ntt-muted">
                        No users match.
                    </div>
                ) : (
                    filtered.map((u) => {
                        const canManage =
                            actor.role === "Super Admin" ||
                            (actor.role === "Admin" && u.role !== "Super Admin");
                        return (
                            <div
                                key={u.user_id}
                                data-testid={`user-row-${u.user_id}`}
                                className="grid grid-cols-[minmax(200px,2fr)_minmax(160px,1.4fr)_120px_100px_120px_160px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center hover:bg-[#FBFCFE]"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="h-8 w-8 rounded-full bg-[#DBEAF6] text-[#0072BC] text-[11px] font-semibold flex items-center justify-center shrink-0">
                                        {(u.full_name || "?")[0]?.toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[13px] font-medium text-[#070F26] truncate flex items-center gap-1.5">
                                            {u.full_name}
                                            {u.is_permanent && (
                                                <Lock className="h-3 w-3 text-ntt-muted" title="Permanent account" />
                                            )}
                                        </div>
                                        <div className="text-[11px] text-ntt-muted truncate">
                                            {u.skills?.length
                                                ? u.skills.slice(0, 3).join(" · ")
                                                : "No skills"}
                                            {u.skills?.length > 3 && ` +${u.skills.length - 3}`}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-[13px] text-[#2B2F3A] truncate">
                                    {u.email}
                                </div>
                                <div>
                                    <StatusPill
                                        label={u.role}
                                        tone={
                                            u.role === "Super Admin"
                                                ? "amber"
                                                : u.role === "Admin"
                                                  ? "blue"
                                                  : u.role === "Project Manager"
                                                    ? "amber"
                                                    : "gray"
                                        }
                                    />
                                </div>
                                <div>
                                    <StatusPill
                                        label={u.active ? "Active" : "Inactive"}
                                        tone={u.active ? "green" : "red"}
                                    />
                                </div>
                                <div>
                                    <StatusPill
                                        label={u.invite_status}
                                        tone={
                                            u.invite_status === "Active"
                                                ? "green"
                                                : "gray"
                                        }
                                    />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    {u.invite_status === "Invited" ? (
                                        <>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                data-testid={`resend-${u.user_id}`}
                                                onClick={() => resend(u)}
                                                className="h-8 text-[12px]"
                                                title="Resend invite (link copied)"
                                            >
                                                <RefreshCw className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                data-testid={`revoke-${u.user_id}`}
                                                onClick={() => revoke(u)}
                                                className="h-8 text-[12px] text-red-600 hover:text-red-700"
                                                title="Revoke invite"
                                            >
                                                <XCircle className="h-3.5 w-3.5" />
                                            </Button>
                                        </>
                                    ) : null}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={!canManage}
                                        data-testid={`edit-${u.user_id}`}
                                        onClick={() => setEditing(u)}
                                        className="h-8 text-[12px]"
                                    >
                                        <Pencil className="h-3.5 w-3.5 mr-1" />
                                        Manage
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <InviteUserDialog
                open={inviteOpen}
                onOpenChange={(v) => {
                    setInviteOpen(v);
                    if (!v) load();
                }}
                actorRole={actor?.role}
            />
            {editing && (
                <EditUserDialog
                    open={!!editing}
                    onOpenChange={(v) => {
                        if (!v) setEditing(null);
                    }}
                    user={editing}
                    actor={actor}
                    projects={projects}
                    allSkills={skills}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                    onSkillsChanged={(next) => setSkills(next)}
                />
            )}
        </Layout>
    );
}
